import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import { CountryISO2CodeSchema, CountryISO3CodeSchema } from '@brightlocal/shared';

/** Schema for a state/province/region within a country */
export const StateSchema = z.object({
  /** Full name of the state (e.g. "California") */
  long: z.string(),
  /** Abbreviated code of the state (e.g. "CA") */
  short: z.string()
});

export const CountrySchema = z
  .object({
    iso_code: CountryISO3CodeSchema,
    iso2_code: CountryISO2CodeSchema,
    name: z.string(),
    phone_code: z.string(),
    states: z.array(StateSchema)
  })
  .transform((data) => camelcaseKeys(data));

export const CountriesResponseSchema = z.object({
  /** List of countries */
  data: z.array(CountrySchema)
});

export const CitySchema = z.object({
  /** City display name */
  city: z.string(),
  /** ISO 3166-1 alpha-3 country code */
  country: CountryISO3CodeSchema,
  /** State/province code, or null if not applicable */
  state: z.string().nullable()
});

export const CitiesResponseSchema = z.object({
  /** List of cities for the requested country */
  data: z.array(CitySchema)
});

export const BusinessCategorySchema = z.object({
  /** Business category unique identifier */
  id: z.number(),
  /** Business category display name */
  name: z.string()
});

export const BusinessCategoriesResponseSchema = z
  .object({
    total_count: z.number(),
    items: z.array(BusinessCategorySchema)
  })
  .transform((data) => camelcaseKeys(data));

export type State = z.infer<typeof StateSchema>;
export type Country = z.infer<typeof CountrySchema>;
export type CountriesResponse = z.infer<typeof CountriesResponseSchema>;
export type City = z.infer<typeof CitySchema>;
export type CitiesResponse = z.infer<typeof CitiesResponseSchema>;
export type BusinessCategory = z.infer<typeof BusinessCategorySchema>;
export type BusinessCategoriesResponse = z.infer<typeof BusinessCategoriesResponseSchema>;
